import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Progress } from "@/app/shared/ui/progress";
import { Switch } from "@/app/shared/ui/switch";
import { useCategories } from "@/hooks/api";
import { useBudgetAnalysis, useUpsertBudget } from "./hooks/useBudgets";
import { addMonths, getCurrentMonthString } from "@/lib/date.utils";
import { toast } from "@/lib/toast";
import { formatDate } from "@/lib/utils";
import type { Category } from "@/types";
import { ChevronRight, Filter, RefreshCw } from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Helper to group categories
function groupCategories(categories: Category[]) {
  const groups = categories.filter((cat) => cat.isGroup && !cat.isDeleted);
  const childCategories = categories.filter(
    (cat) => !cat.isGroup && !cat.isDeleted && !cat.isIncome,
  );

  const grouped: Array<{ group: Category | null; categories: Category[] }> = [];

  // Add categories with groups
  for (const group of groups) {
    const children = childCategories.filter((cat) => cat.groupId === group.id);
    if (children.length > 0) {
      grouped.push({ group, categories: children });
    }
  }

  // Add ungrouped categories
  const ungrouped = childCategories.filter((cat) => !cat.groupId);
  if (ungrouped.length > 0) {
    grouped.push({ group: null, categories: ungrouped });
  }

  return grouped;
}

export function Budgets() {
  const navigate = useNavigate();

  // Use URL query params for month (YYYY-MM format)
  const [monthParam, setMonthParam] = useQueryState(
    "month",
    parseAsString.withDefault(getCurrentMonthString()),
  );

  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [editingCell, setEditingCell] = useState<{
    categoryId: number;
    value: string;
  } | null>(null);

  const {
    data: analysis,
    isLoading,
    error,
    refetch,
  } = useBudgetAnalysis(monthParam, includeExcluded);
  const { data: categories = [] } = useCategories();
  const upsertBudget = useUpsertBudget();

  const groupedCategories = groupCategories(categories);

  const toggleGroup = (groupId: number) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupId)) {
      newCollapsed.delete(groupId);
    } else {
      newCollapsed.add(groupId);
    }
    setCollapsedGroups(newCollapsed);
  };

  const handleCellClick = (categoryId: number, currentAmount: number) => {
    setEditingCell({
      categoryId,
      value: String(currentAmount || ""),
    });
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const amount = Number(editingCell.value);
    if (amount && amount > 0) {
      try {
        await upsertBudget.mutateAsync({
          categoryId: editingCell.categoryId,
          month: monthParam,
          amount,
        });
      } catch {
        toast({
          title: "Error",
          description: "Failed to save budget",
          variant: "destructive",
        });
      }
    }
    setEditingCell(null);
  };

  const handleCategoryRowClick = (categoryId: number) => {
    const [year, month] = monthParam.split("-").map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    navigate(
      `/transactions?categoryId=${categoryId}&startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`,
    );
  };

  if (isLoading && !analysis) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-4 border-border border-t-border" />
        <p className="mt-4 text-sm text-muted-foreground">Loading budgets...</p>
      </div>
    );
  }

  return (
    // Full viewport height minus the shell's mobile top bar (h-14 = 3.5rem,
    // hidden on lg+) — avoids a dead band below the pinned footer on desktop.
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-border bg-background flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonthParam(addMonths(monthParam, -1))}
            className="size-8 p-0"
          >
            <ChevronRight className="size-4 rotate-180" />
          </Button>
          <h1 className="text-lg font-medium min-w-[160px] text-center">
            {analysis?.monthDisplay || monthParam}
          </h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonthParam(addMonths(monthParam, 1))}
            className="size-8 p-0"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 px-3 py-1 border border-border rounded-md h-8">
            <Filter className="size-3.5 text-muted-foreground" />
            <Switch
              id="include-excluded"
              checked={includeExcluded}
              onCheckedChange={setIncludeExcluded}
              className="scale-75"
            />
            <Label htmlFor="include-excluded" className="text-xs cursor-pointer whitespace-nowrap">
              Include Excluded
            </Label>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw className={`size-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Error loading budget data</p>
          <p className="mt-1 text-xs text-destructive">
            {error instanceof Error ? error.message : "An error occurred"}
          </p>
        </div>
      )}

      {/* Table */}
      {analysis && (
        <div className="flex-1 overflow-auto">
          <div className="w-full md:min-w-[820px]">
            {/* Summary Row */}
            <div className="sticky top-0 z-10 bg-muted border-b border-border backdrop-blur-sm">
              {/* Desktop Header */}
              <div className="hidden md:grid grid-cols-[minmax(200px,1fr)_120px_120px_120px_60px] gap-2 px-4 py-3 text-sm">
                <div className="font-medium text-foreground">Category</div>
                <div className="text-right font-medium text-foreground">Budgeted</div>
                <div className="text-right font-medium text-foreground">Spent</div>
                <div className="text-right font-medium text-foreground">Remaining</div>
                <div />
              </div>
              {/* Mobile Header */}
              <div className="flex md:hidden justify-between px-4 py-3 text-sm">
                <div className="font-medium text-foreground">Category</div>
                <div className="font-medium text-foreground">Remaining</div>
              </div>
            </div>

            {/* Category Groups */}
            <div className="divide-y divide-border">
              {groupedCategories.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="font-medium">No categories available</p>
                  <p className="text-sm mt-1">Create categories in Settings to start budgeting</p>
                </div>
              ) : (
                groupedCategories.map((group, groupIndex) => {
                  const groupId = group.group?.id || -groupIndex;
                  const isCollapsed = collapsedGroups.has(groupId);

                  // Calculate group totals
                  const groupBudget = group.categories.reduce((sum, cat) => {
                    const budget = analysis.categoriesAnalysis.find((b) => b.categoryId === cat.id);
                    return sum + (budget?.budgetAmount || 0);
                  }, 0);

                  const groupSpent = group.categories.reduce((sum, cat) => {
                    const budget = analysis.categoriesAnalysis.find((b) => b.categoryId === cat.id);
                    return sum + (budget?.actualSpent || 0);
                  }, 0);

                  const groupRemaining = groupBudget - groupSpent;

                  return (
                    <div key={groupId}>
                      {/* Group Header */}
                      <div
                        className="flex justify-between items-center md:grid md:grid-cols-[minmax(200px,1fr)_120px_120px_120px_60px] gap-2 px-4 py-2 bg-muted hover:bg-muted cursor-pointer"
                        onClick={() => toggleGroup(groupId)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            toggleGroup(groupId);
                          }
                        }}
                        // biome-ignore lint/a11y/useSemanticElements: simplified clickable div
                        role="button"
                        tabIndex={0}
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={`size-4 transition-transform ${!isCollapsed ? "rotate-90" : ""}`}
                          />
                          <span className="font-medium text-sm text-foreground">
                            {group.group ? group.group.name : "Other"}
                          </span>
                        </div>
                        {/* Desktop: Budget */}
                        <div className="hidden md:block text-right text-sm font-medium text-foreground">
                          {groupBudget > 0 && <AmountDisplay amount={groupBudget} compact />}
                        </div>
                        {/* Desktop: Spent */}
                        <div className="hidden md:block text-right text-sm font-medium text-foreground">
                          {groupSpent > 0 && <AmountDisplay amount={groupSpent} compact />}
                        </div>
                        {/* Remaining (Both) */}
                        <div
                          className={`text-right text-sm font-medium ${groupRemaining >= 0 ? "text-success" : "text-destructive"}`}
                        >
                          {(groupBudget > 0 || groupSpent > 0) && (
                            <AmountDisplay amount={Math.abs(groupRemaining)} compact />
                          )}
                        </div>
                        <div className="hidden md:block" />
                      </div>

                      {/* Category Rows */}
                      {!isCollapsed &&
                        group.categories.map((category) => {
                          const budget = analysis.categoriesAnalysis.find(
                            (b) => b.categoryId === category.id,
                          );
                          const budgetAmount = budget?.budgetAmount || 0;
                          const actualSpent = budget?.actualSpent || 0;
                          const remaining = budget?.remaining || 0;
                          const isEditing = editingCell?.categoryId === category.id;

                          return (
                            <div
                              key={category.id}
                              className="flex flex-col md:grid md:grid-cols-[minmax(200px,1fr)_120px_120px_120px_60px] gap-2 px-4 py-2 hover:bg-accent/30 group border-b md:border-b-0 last:border-0"
                            >
                              {/* Row Top (Mobile) / Name Cell (Desktop) */}
                              <div className="flex justify-between md:contents">
                                <div
                                  className="flex items-center gap-2 text-sm cursor-pointer flex-1"
                                  onClick={() => handleCategoryRowClick(category.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      handleCategoryRowClick(category.id);
                                    }
                                  }}
                                  // biome-ignore lint/a11y/useSemanticElements: simplified clickable div
                                  role="button"
                                  tabIndex={0}
                                >
                                  <span className="md:pl-6 group-hover:text-primary">
                                    {category.name}
                                  </span>
                                </div>

                                {/* Mobile Remaining */}
                                <div
                                  className={`md:hidden text-right text-sm font-medium ${remaining >= 0 ? "text-success" : "text-destructive"}`}
                                >
                                  {budgetAmount > 0 && (
                                    <AmountDisplay amount={Math.abs(remaining)} compact />
                                  )}
                                </div>
                              </div>

                              {/* Budgeted Cell - Editable */}
                              {/* On mobile, this will be row 2 left */}
                              <div className="md:contents">
                                {isEditing ? (
                                  /* Edit Mode */
                                  <div className="col-span-1 md:text-right text-sm">
                                    <Input
                                      type="number"
                                      value={editingCell.value}
                                      onChange={(e) =>
                                        setEditingCell({
                                          ...editingCell,
                                          value: e.target.value,
                                        })
                                      }
                                      onBlur={handleCellBlur}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleCellBlur();
                                        if (e.key === "Escape") setEditingCell(null);
                                      }}
                                      autoFocus
                                      className="h-7 md:text-right"
                                    />
                                  </div>
                                ) : (
                                  /* Display Mode */
                                  <div className="flex justify-between items-center md:contents text-xs md:text-sm">
                                    {/* Mobile: Spent / Budgeted */}
                                    <div className="flex items-center gap-1 md:hidden text-muted-foreground">
                                      <span>
                                        {actualSpent > 0 ? (
                                          <AmountDisplay amount={actualSpent} compact />
                                        ) : (
                                          "—"
                                        )}
                                      </span>
                                      <span>/</span>
                                      <div
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCellClick(category.id, budgetAmount);
                                        }}
                                        // biome-ignore lint/a11y/useSemanticElements: simplified clickable div
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.stopPropagation();
                                            handleCellClick(category.id, budgetAmount);
                                          }
                                        }}
                                        className="cursor-text hover:text-primary underline decoration-dotted underline-offset-2"
                                      >
                                        {budgetAmount > 0 ? (
                                          <AmountDisplay amount={budgetAmount} compact />
                                        ) : (
                                          "Set Budget"
                                        )}
                                      </div>
                                    </div>

                                    {/* Desktop: Budgeted Cell */}
                                    <div className="hidden md:block text-right text-sm">
                                      <div
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCellClick(category.id, budgetAmount);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.stopPropagation();
                                            handleCellClick(category.id, budgetAmount);
                                          }
                                        }}
                                        // biome-ignore lint/a11y/useSemanticElements: simplified clickable div
                                        role="button"
                                        tabIndex={0}
                                        className="h-7 px-2 rounded cursor-text hover:bg-info/10 hover:border hover:border-info/20 flex items-center justify-end group/cell"
                                        title="Click to edit budget"
                                      >
                                        {budgetAmount > 0 ? (
                                          <AmountDisplay amount={budgetAmount} compact />
                                        ) : (
                                          <span className="text-muted-foreground text-xs group-hover/cell:text-info">
                                            Click to set
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Desktop: Spent Cell */}
                                    <div className="hidden md:flex text-right text-sm items-center justify-end h-7 px-2">
                                      {actualSpent > 0 ? (
                                        <AmountDisplay amount={actualSpent} compact />
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </div>

                                    {/* Desktop: Remaining Cell */}
                                    <div
                                      className={`hidden md:flex text-right text-sm items-center justify-end h-7 px-2 font-medium ${remaining >= 0 ? "text-success" : "text-destructive"}`}
                                    >
                                      {budgetAmount > 0 && (
                                        <AmountDisplay amount={Math.abs(remaining)} compact />
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Progress indicator (Shared) */}
                              <div className="flex items-center w-full mt-1 md:mt-0">
                                {budgetAmount > 0 && (
                                  <div className="w-full">
                                    <Progress
                                      value={Math.min((actualSpent / budgetAmount) * 100, 100)}
                                      className="h-1"
                                      indicatorClassName={
                                        actualSpent > budgetAmount
                                          ? "bg-destructive"
                                          : actualSpent / budgetAmount >= 0.9
                                            ? "bg-warning"
                                            : "bg-success"
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Totals */}
            {analysis.categoriesAnalysis.length > 0 && (
              <div className="sticky bottom-0 bg-muted/80 border-t backdrop-blur-sm">
                <div className="flex justify-between md:grid md:grid-cols-[minmax(200px,1fr)_120px_120px_120px_60px] gap-2 px-4 py-3">
                  <div className="font-semibold">Total</div>
                  {/* Desktop Columns */}
                  <div className="hidden md:block text-right font-semibold">
                    <AmountDisplay amount={analysis.totalBudgeted} compact />
                  </div>
                  <div className="hidden md:block text-right font-semibold">
                    <AmountDisplay amount={analysis.totalSpent} compact />
                  </div>
                  {/* Remaining (Both) */}
                  <div
                    className={`text-right font-semibold ${analysis.totalRemaining >= 0 ? "text-success" : "text-destructive"}`}
                  >
                    <AmountDisplay amount={Math.abs(analysis.totalRemaining)} compact />
                  </div>
                  <div className="hidden md:block" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
